/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { RatingItem } from './RatingItem';
import styles from './styles.module.scss';
import { useLabels } from '@/hooks/useLabels';

export type RatingProps = {
  name: string;
  id: string;
  labelID: string;
  value: number | null;
  defaultValue?: number;
  disabled?: boolean;
  className?: string;
  max?: number;
  required?: boolean;
  activeClassName?: string;
  hoveredClassName?: string;
  disabledClassName?: string;
  selectedClassName?: string;
  iconClassName?: string;
  inputClassName?: string;
  icon?: React.ReactNode;
  autoFocus?: boolean;
  onChange: (value: number | null) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  getA11yLabel?: (value: number | null) => string | null;
};

const MIN = 1;
const MAX = 5;

const labelMap = {
  star: 'label_reviews_star',
  stars: 'label_reviews_stars',
};

export const Rating = (props: RatingProps) => {
  const {
    value: valueProp,
    disabled = false,
    max = MAX,
    icon,
    className,
    hoveredClassName,
    disabledClassName,
    selectedClassName,
    iconClassName,
    inputClassName,
    name,
    autoFocus,
    onChange,
    onBlur,
    getA11yLabel,
    required,
  } = props;

  const labels = useLabels(labelMap);
  const [hover, setHover] = useState(-1);

  const radioGroupRef = useRef<HTMLDivElement>(null);

  let value = valueProp;

  if (hover !== -1) {
    value = hover;
  }

  const handleMouseLeave = () => {
    const newHover = -1;
    setHover(newHover);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? null : parseFloat(event.target.value);

    onChange(newValue ?? MIN);
  };

  const inputNum = useMemo(() => {
    return Array.from({ length: max }, (_, index) => {
      return index + 1;
    });
  }, [max]);

  const a11yLabels = useMemo(() => {
    return inputNum.map(
      (starNum) =>
        getA11yLabel?.(starNum) ?? `${starNum} ${starNum === 1 ? labels.star : labels.stars}`
    );
  }, [getA11yLabel, inputNum, labels.star, labels.stars]);

  const onMouseEnter = (event: React.MouseEvent<HTMLInputElement>, value: number) => {
    setHover(value);
  };

  return (
    <div
      className={cx(styles.root, className)}
      onMouseLeave={handleMouseLeave}
      id={props.id}
      role="radiogroup"
      aria-required={required}
      aria-labelledby={props.labelID}
      ref={radioGroupRef}
      onBlur={(e) => {
        // Call `onBlur` when none of the radio buttons are focused
        requestAnimationFrame(() => {
          if (radioGroupRef.current && !radioGroupRef.current.contains(document.activeElement)) {
            onBlur?.(e);
          }
        });
      }}
    >
      {inputNum.map((itemValue, index) => {
        return (
          <RatingItem
            key={itemValue}
            id={`field-${name}-${itemValue}`}
            label={a11yLabels[index]}
            icon={icon}
            disabled={disabled}
            hover={hover}
            name={name}
            onChange={handleChange}
            onMouseEnter={onMouseEnter}
            ratingValue={value}
            selectedValue={valueProp}
            itemValue={itemValue}
            hoveredClassName={hoveredClassName}
            disabledClassName={disabledClassName}
            selectedClassName={selectedClassName}
            iconClassName={iconClassName}
            inputClassName={inputClassName}
            autoFocus={autoFocus && index === 0}
          />
        );
      })}
    </div>
  );
};
